import { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';

import { generateErrorMessages } from 'utils/error';
import { useQueryParams, encodeQueryParams } from 'use-query-params';
import { stringify } from 'query-string';
import { Pagination } from 'components/Pagination';
import { Filters } from './Filters';
import { useGeneral } from 'context/general';
import { convertBytesToIEC } from 'utils/bytes';
import { getFormattedFIL, getPercents } from 'utils/numbers';
import { dashboardQueryTypes } from 'utils/query';
import { getCreditScoreText, getReachabilityName } from 'utils/strings';
import { SortButton } from 'components/SortButton';
import { Spinner } from 'components/Spinner';
import { Tooltip } from 'components/Tooltip';
import { Svg } from 'components/Svg';

import {
  defaultColumns,
  limitDefault,
  versionStorageName,
  columnsStorageName,
  columnsVersion,
} from 'constant';

// eslint-disable-next-line css-modules/no-unused-class
import s from './s.module.css';

const GoldenPath = ({ goldenPath }) => {
  const availableKeys = Object.keys(goldenPath || {}).filter(
    (key) => key !== 'maxSizeChunks'
  );

  const { completed, failed, unavailable } = availableKeys.reduce(
    (acc, key) => {
      if (goldenPath[key]) {
        return { ...acc, completed: [...acc.completed, key] };
      }
      if (goldenPath[key] === false) {
        return { ...acc, failed: [...acc.failed, key] };
      }
      return { ...acc, unavailable: [...acc.unavailable, key] };
    },
    {
      completed: [],
      failed: [],
      unavailable: [],
    }
  );

  const getTitle = (key) => {
    switch (key) {
      case 'storageDealSuccessRate':
        return 'Storage DSR > 90%';
      case 'maxSizeChunks':
        return '32GiB chunks';
      case 'verifiedDealNoPrice':
        return 'Verified deals price = 0';
      case 'retrievalDealSuccessRate':
        return 'Retrieval DSR > 90%';
      case 'fastRetrieval':
        return 'Fast retrieval';
      case 'faultsBelowThreshold':
        return 'Few sector faults and slashes';
      default:
        return key;
    }
  };

  const isGray = unavailable.length;
  const isGreen = !isGray && completed.length / availableKeys.length >= 0.75;
  const isRed = !isGray && failed.length / availableKeys.length > 0.5;
  const isYellow = !isGray && !isRed && !isGreen;

  return (
    <Tooltip
      target={
        <span
          className={cn('highlight', {
            green: isGreen,
            yellow: isYellow,
            red: isRed,
            gray: isGray,
          })}
        >
          {completed.length}/{availableKeys.length}
        </span>
      }
    >
      {failed.map((key) => (
        <div key={key} className={s.goldenPathRow}>
          <Svg id="failed" size={16} />
          <span className={s.goldenPathTitle}>{getTitle(key)}</span>
        </div>
      ))}
      {completed.map((key) => (
        <div key={key} className={s.goldenPathRow}>
          <Svg id="verified" size={16} />
          <span className={s.goldenPathTitle}>{getTitle(key)}</span>
        </div>
      ))}
      {unavailable.length ? (
        <>
          <div className={cn(s.goldenPathRow, s.goldenPathUnavailableTitle)}>
            UNAVAILABLE DATA
          </div>
          {unavailable.map((key) => (
            <div key={key} className={s.goldenPathRow}>
              <Svg id="unavailable" size={16} />
              <span className={s.goldenPathTitle}>{getTitle(key)}</span>
            </div>
          ))}
        </>
      ) : null}
    </Tooltip>
  );
};

export default function HomePage() {
  const [columns, setColumns] = useState(defaultColumns);
  const { api, notificationActions } = useGeneral();

  const [data, setData] = useState(() => ({
    miners: [],
    pagination: {
      limit: limitDefault,
      offset: 0,
      total: 0,
    },
    loaded: false,
  }));

  const [query] = useQueryParams(dashboardQueryTypes);
  const apiQueryParams = useMemo(() => {
    if (query) {
      const { offset, limit, sortBy, order, search, region } = query;
      return { offset, limit, sortBy, order, search, region };
    }
    return {};
  }, [query]);

  useEffect(() => {
    setData((prevState) => ({ ...prevState, loaded: false }));
    const abortController = new AbortController();
    const encodedQuery = encodeQueryParams(dashboardQueryTypes, {
      ...apiQueryParams,
      offset: query.offset || 0,
      limit: query.limit || limitDefault,
    });

    api(`/miners?${stringify(encodedQuery)}`, {
      signal: abortController.signal,
    })
      .then((receivedMiners) => {
        setData({ ...receivedMiners, loaded: true });
      })
      .catch((err) => {
        if (abortController.signal.aborted) {
          return;
        }
        console.error(err);
        const errors = generateErrorMessages(err);
        errors.forEach((er) =>
          notificationActions.addNotification(er, {
            level: 'error',
          })
        );
        setData((prevState) => ({ ...prevState, loaded: false }));
      });

    return function cancel() {
      abortController.abort();
    };
  }, [query]);

  const handlerChangeColumns = (newColumns) => {
    window.localStorage.setItem(versionStorageName, String(columnsVersion));
    window.localStorage.setItem(columnsStorageName, JSON.stringify(newColumns));
    setColumns(newColumns);
  };

  useEffect(() => {
    const userColumnsVersion = window.localStorage.getItem(versionStorageName);
    const userColumns = window.localStorage.getItem(columnsStorageName);

    if (
      userColumns &&
      userColumnsVersion &&
      Number(userColumnsVersion) === Number(columnsVersion)
    ) {
      setColumns(JSON.parse(userColumns));
    } else {
      window.localStorage.setItem(versionStorageName, String(columnsVersion));
      window.localStorage.setItem(columnsStorageName, JSON.stringify(columns));
    }
  }, []);

  useEffect(() => {
    if (query.columns?.length > 0) {
      setColumns((prevColumns) =>
        prevColumns.map((prevColumn) =>
          query.columns?.indexOf(prevColumn.cuid) !== -1
            ? { ...prevColumn, selected: true }
            : prevColumn
        )
      );
    }
  }, [query]);

  return (
    <>
      <h2 className="title">Filecoin Reputation System</h2>
      {/*<p className={s.intro}>*/}
      {/*  Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla quam*/}
      {/*  velit, vulputate eu pharetra nec, mattis ac neque. Duis vulputate*/}
      {/*  commodo lectus, ac blandit elit tincidunt id. Sed rhoncus, tortor sed*/}
      {/*  eleifend tristique*/}
      {/*</p>*/}
      <Filters columns={columns} onChangeColumns={handlerChangeColumns} />
      <div className={s.spinnerContainer}>
        {!data.loaded && <Spinner className={s.spinner} />}

        <div className={s.tableWrap}>
          <table className="table">
            <thead>
              <tr>
                {columns.map(({ key, name, selected, alignRight, sortKey }) => {
                  if (!selected) {
                    return null;
                  }

                  const getCellValue = (fieldKey) => {
                    switch (fieldKey) {
                      case 'creditScore':
                        return (
                          <Tooltip target={name} placement="bottom">
                            The credit score measures the ability of a Storage
                            Provider to pay back the loans taken on the{' '}
                            <a
                              href="https://filecoin-lending.com/"
                              style={{
                                textDecoration: 'underline',
                                color: 'inherit',
                              }}
                            >
                              filecoin-lending.com
                            </a>{' '}
                            platform. Check out how the score is calculated
                            here:{' '}
                            <a
                              href="https://filecoin-lending.com/read-more"
                              style={{
                                textDecoration: 'underline',
                                color: 'inherit',
                              }}
                            >
                              https://filecoin-lending.com/read-more
                            </a>
                          </Tooltip>
                        );
                      case 'reachability':
                        return (
                          <Tooltip target={name} placement="bottom">
                            The reachability metric indicates either or not the
                            miner responds to lotus client query-ask. Please
                            note that a miner who is unreachable might accept
                            offline deals.
                          </Tooltip>
                        );
                      case 'minPieceSize':
                        return (
                          <Tooltip target={name} placement="bottom">
                            The minimum file size accepted by the miners.
                          </Tooltip>
                        );
                      case 'maxPieceSize':
                        return (
                          <Tooltip target={name} placement="bottom">
                            The maximum file size accepted by the miners.
                          </Tooltip>
                        );
                      case 'rank':
                        return (
                          <Tooltip target={name} placement="bottom">
                            The default rank is based on reputation score and
                            raw power. This is just the platform's way of
                            listing the miners, a difference in ranking does not
                            necessarily mean a difference in service quality.
                          </Tooltip>
                        );
                      case 'tag.name':
                        return (
                          <Tooltip target={name} placement="bottom">
                            The miner’s tag from Filfox
                          </Tooltip>
                        );
                      case 'storageDeals.recent30days':
                        return (
                          <span style={{ display: 'inline-flex' }}>
                            <Tooltip target={name} placement="bottom">
                              Deals made in the last 30 days
                            </Tooltip>
                            <SortButton fieldKey={sortKey} />
                          </span>
                        );
                      case 'address':
                      case 'isoCode':
                      case 'region':
                      case 'storageDeals.total':
                      case 'storageDeals.successRate':
                      case 'storageDeals.slashed':
                      case 'rawPower':
                      case 'qualityAdjPower':
                      case 'verifiedPrice':
                      case 'price':
                      case 'freeSpace':
                      // case 'uptimeAverage':
                      case 'storageDeals.averagePrice':
                      case 'storageDeals.noPenalties':
                      case 'storageDeals.dataStored':
                      case 'energy':
                        if (sortKey) {
                          return (
                            <SortButton fieldKey={sortKey}>{name}</SortButton>
                          );
                        }
                        return name;
                      default:
                        return null;
                    }
                  };

                  return (
                    <th key={key} className={cn({ alignRight })}>
                      {getCellValue(key)}
                    </th>
                  );
                })}
                <th className="alignRight">
                  <SortButton fieldKey="goldenPath">
                    Deal Success Predictors
                  </SortButton>
                </th>
                <th className="alignRight">
                  <SortButton fieldKey="score">Reputation Score</SortButton>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.loaded && !data.miners.length && (
                <tr>
                  <td
                    colSpan={
                      columns.filter(({ selected }) => selected).length + 1
                    }
                    align="center"
                  >
                    No data
                  </td>
                </tr>
              )}
              {data.miners.map((miner) => {
                const minerScore = Number(miner.score);

                return (
                  <tr key={miner.address}>
                    {columns.map(({ key, selected, alignRight }) => {
                      if (!selected) {
                        return null;
                      }

                      const getCellValue = (fieldKey) => {
                        switch (fieldKey) {
                          case 'creditScore':
                            return (
                              <span
                                className={cn('creditScore', miner[fieldKey])}
                              >
                                {getCreditScoreText(miner[fieldKey])}
                              </span>
                            );
                          case 'rank':
                            return (
                              <span className="rank">{miner[fieldKey]}</span>
                            );
                          case 'address':
                            return (
                              <span className="entity">{miner[fieldKey]}</span>
                            );
                          case 'tag.name':
                            return (
                              <>
                                {miner.tag.name || 'N/A'}{' '}
                                {miner.tag.verified ? (
                                  <Svg
                                    id="verified"
                                    width={16}
                                    height={16}
                                    style={{ verticalAlign: 'bottom' }}
                                  />
                                ) : null}
                              </>
                            );
                          case 'isoCode':
                          case 'region':
                            return miner[fieldKey];
                          case 'reachability':
                            return (
                              <span
                                className={cn('reachability', miner[fieldKey])}
                              >
                                {getReachabilityName(miner[fieldKey])}
                              </span>
                            );
                          case 'storageDeals.averagePrice':
                            return getFormattedFIL(
                              miner.storageDeals.averagePrice
                            );
                          case 'storageDeals.total':
                            return miner.storageDeals.total || 'N/A';
                          case 'storageDeals.noPenalties':
                            return miner.storageDeals.noPenalties || 'N/A';
                          case 'storageDeals.successRate':
                            return getPercents(miner.storageDeals.successRate);
                          case 'storageDeals.slashed':
                            return miner.storageDeals.slashed || 'N/A';
                          case 'storageDeals.dataStored':
                            return convertBytesToIEC(
                              miner.storageDeals.dataStored
                            );
                          case 'storageDeals.recent30days':
                            return typeof miner.storageDeals.recent30days ===
                              'number'
                              ? miner.storageDeals.recent30days
                              : 'N/A';
                          case 'energy':
                            const formattedRecs =
                              typeof miner.energy.recs === 'number'
                                ? `${miner.energy.recs} MWh`
                                : 'N/A';
                            if (miner.energy.pageUrl) {
                              return (
                                <a
                                  href={miner.energy.pageUrl}
                                  className="link"
                                  target="_blank"
                                  rel="noreferrer noopener"
                                >
                                  {formattedRecs}
                                </a>
                              );
                            }
                            return formattedRecs;
                          // case 'uptimeAverage':
                          //   return getPercents(miner[fieldKey]);
                          case 'minPieceSize':
                          case 'maxPieceSize':
                          case 'rawPower':
                          case 'qualityAdjPower':
                          case 'freeSpace':
                            return convertBytesToIEC(miner[fieldKey]);
                          case 'verifiedPrice':
                          case 'price':
                            return getFormattedFIL(miner[fieldKey]);
                          default:
                            return null;
                        }
                      };

                      return (
                        <td key={key} className={cn({ alignRight })}>
                          {getCellValue(key)}
                        </td>
                      );
                    })}
                    <td className="alignRight">
                      <GoldenPath goldenPath={miner.goldenPath} />
                    </td>
                    <td
                      className={cn('alignRight', 'highlight', {
                        green: minerScore >= 80,
                        yellow: minerScore < 80 && minerScore >= 40,
                        red: minerScore < 40,
                      })}
                    >
                      <Tooltip target={minerScore}>
                        Online Reachability Score {miner.scores.uptime}
                        <br />
                        Commited Sectors Proofs Score{' '}
                        {miner.scores.committedSectorsProofs}
                        <br />
                        Storage Deals Score {miner.scores.storageDeals}
                      </Tooltip>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <Pagination total={data.pagination.total} />
    </>
  );
}
